import { useCallback, useEffect, useRef, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Spinner from 'react-bootstrap/Spinner'
import Table from 'react-bootstrap/Table'
import { toast } from 'react-toastify'

import { toastErrorLog } from '../../helpers/toastErrorLog'
import { useAuth } from '../../hooks/useAuth'
import * as cuentaCorrienteService from '../servicios/cuentaCorrienteService'
import BordePrincipal from '../ui/BordePrincipal'
import Toggle from '../ui/Toggle'
import styles from './CuentaCorrienteMaestro.module.css'

const CONDICIONES_PAGO = [
  { value: 7, label: '7 días' },
  { value: 15, label: '15 días' },
  { value: 30, label: '30 días' },
  { value: 45, label: '45 días' },
  { value: 60, label: '60 días' },
  { value: 90, label: '90 días' }
]

const PAGE_SIZE = 15
const DEBOUNCE_MS = 400
const COLS_ADMIN = 7
const COLS_NORMAL = 6
const MSJ_FACTURA_VENCIDA = [
  'Su cuenta corriente está deshabilitada ya que tiene facturas vencidas sin pagar en e-BuyPlace.',
  'Los compradores no podrán comprarle por cuenta corriente hasta que regularice su situación.'
]

const nombreEmpresa = (row) => (row.alias ? `${row.razonSocial} (${row.alias})` : row.razonSocial)

const CuentaCorrienteMaestro = () => {
  const { loginDataContext: { empid: empidVendedor } = {}, esPerfilAdmin } = useAuth()
  const [datosCC, setDatosCC] = useState(null)
  const [paginaActual, setPaginaActual] = useState(1)
  const [cargando, setCargando] = useState(false)
  const [busqueda, setBusqueda] = useState('')
  const [busquedaActiva, setBusquedaActiva] = useState('')
  const [pendientes, setPendientes] = useState({})
  const [guardando, setGuardando] = useState({})
  const [condicionBulk, setCondicionBulk] = useState('')
  const [aplicandoBulk, setAplicandoBulk] = useState(false)
  const debounceRef = useRef(null)

  const { total = 0, totalHabilitados = 0, rows = [] } = datosCC?.empresas ?? {}
  const tieneFacturaVencida = Boolean(datosCC?.tieneFacturaVencida)
  const puedeAdministrar = esPerfilAdmin && !tieneFacturaVencida
  const totalPaginas = Math.ceil(total / PAGE_SIZE) || 1

  const cargarEmpresas = useCallback(
    async (pagina, termino) => {
      if (!empidVendedor) return
      setCargando(true)
      try {
        const { data } = await cuentaCorrienteService.obtenerEmpresas(
          empidVendedor,
          pagina,
          PAGE_SIZE,
          termino
        )
        setDatosCC(data.body ?? null)
        setPendientes({})
      } catch (error) {
        toastErrorLog(error, 'Error al cargar las empresas.', 'cuentaCorriente')
      } finally {
        setCargando(false)
      }
    },
    [empidVendedor]
  )

  useEffect(() => {
    cargarEmpresas(paginaActual, busquedaActiva)
  }, [paginaActual, busquedaActiva, cargarEmpresas])

  const handleBusquedaChange = (e) => {
    const valor = e.target.value
    setBusqueda(valor)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      setPaginaActual(1)
      setBusquedaActiva(valor.trim())
    }, DEBOUNCE_MS)
  }

  const getPendiente = (empId, row) => {
    const ccm = row.cuentasCorrientesComprador
    const original = { habilita: Boolean(ccm?.habilita), condicionPago: ccm?.condicionPago ?? null }
    const actual = pendientes[empId] ?? original
    const estaModificado =
      pendientes[empId] !== undefined &&
      (actual.habilita !== original.habilita || actual.condicionPago !== original.condicionPago)
    const puedeGuardar =
      estaModificado && (original.habilita || (actual.habilita && Boolean(actual.condicionPago)))
    return { ...actual, estaModificado, puedeGuardar }
  }

  const handleToggle = (empId, row) => {
    const actual = getPendiente(empId, row)
    setPendientes((prev) => ({ ...prev, [empId]: { ...actual, habilita: !actual.habilita } }))
  }

  const handleCondicionPago = (empId, row, valor) => {
    if (valor === '') return
    const actual = getPendiente(empId, row)
    setPendientes((prev) => ({
      ...prev,
      [empId]: { ...actual, condicionPago: Number(valor) }
    }))
  }

  const handleGuardarFila = async (empId, row) => {
    const datos = getPendiente(empId, row)
    setGuardando((prev) => ({ ...prev, [empId]: true }))
    try {
      await cuentaCorrienteService.guardarHabilitacion({
        empidVendedor,
        empidComprador: empId,
        habilita: datos.habilita,
        condicionPago: datos.condicionPago ?? null
      })
      toast.success('Registro guardado correctamente')
      await cargarEmpresas(paginaActual, busquedaActiva)
    } catch (error) {
      toastErrorLog(
        error,
        'Error al guardar la habilitación de cuenta corriente.',
        'cuentaCorriente'
      )
    } finally {
      setGuardando((prev) => ({ ...prev, [empId]: false }))
    }
  }

  const handleAplicarBulk = async () => {
    if (condicionBulk === '') return
    setAplicandoBulk(true)
    try {
      await cuentaCorrienteService.guardarHabilitacionMasiva(empidVendedor, Number(condicionBulk))
      toast.success('Condición de pago aplicada a todos los registros habilitados')
      await cargarEmpresas(paginaActual, busquedaActiva)
      setCondicionBulk('')
    } catch (error) {
      toastErrorLog(error, 'Error al aplicar la condición masiva.', 'cuentaCorriente')
    } finally {
      setAplicandoBulk(false)
    }
  }

  return (
    <section className="pageCard pageCard--conMargenInferior">
      <div className={styles.encabezado}>
        <h2 className={styles.titulo}>Cuenta Corriente</h2>
        <span>
          <Badge bg="success" className={`me-1 ${styles.badgeHabilitados}`}>
            {totalHabilitados} habilitado{totalHabilitados !== 1 ? 's' : ''}
          </Badge>
          <Badge bg="secondary" className={styles.badgeHabilitados}>
            {total} empresa{total !== 1 ? 's' : ''}
          </Badge>
        </span>
      </div>

      {tieneFacturaVencida && (
        <Alert variant="warning" className="d-flex align-items-start gap-2">
          <i className="bi bi-exclamation-triangle-fill mt-1" aria-hidden="true" />
          <div>
            {MSJ_FACTURA_VENCIDA.map((linea) => (
              <p key={linea} className="mb-0">
                {linea}
              </p>
            ))}
          </div>
        </Alert>
      )}

      {totalHabilitados > 0 && puedeAdministrar && (
        <BordePrincipal titulo="Aplicar condición de pago a todos los habilitados">
          <InputGroup style={{ maxWidth: '400px' }}>
            <Form.Select
              value={condicionBulk}
              onChange={(e) => setCondicionBulk(e.target.value)}
              aria-label="Condición de pago masiva"
            >
              <option value="">— Seleccionar condición —</option>
              {CONDICIONES_PAGO.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Form.Select>
            <Button
              variant="primary"
              className={styles.botonPrimario}
              onClick={handleAplicarBulk}
              disabled={condicionBulk === '' || aplicandoBulk}
            >
              {aplicandoBulk ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Aplicando...
                </>
              ) : (
                'Aplicar a todos'
              )}
            </Button>
          </InputGroup>
        </BordePrincipal>
      )}

      <BordePrincipal titulo="Empresas" className={styles.bordePrincipal}>
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <i className="bi bi-search" aria-hidden="true" />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Filtrar por razón social o EmpId..."
            value={busqueda}
            onChange={handleBusquedaChange}
            aria-label="Filtrar empresas"
          />
          {cargando && (
            <InputGroup.Text>
              <Spinner animation="border" size="sm" role="status" aria-label="Cargando" />
            </InputGroup.Text>
          )}
        </InputGroup>

        <div className={styles.tablaWrapper}>
          <Table hover responsive size="sm" className={styles.tabla}>
            <thead>
              <tr>
                <th>Razón Social</th>
                <th>EmpId</th>
                <th className="text-center">Habilitado</th>
                <th>Condición de pago</th>
                {puedeAdministrar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {!cargando && rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={puedeAdministrar ? COLS_ADMIN : COLS_NORMAL}
                    className="text-center text-muted py-3"
                  >
                    {busquedaActiva
                      ? 'No se encontraron empresas para la búsqueda.'
                      : 'No hay empresas disponibles.'}
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const empId = String(row.empId)
                  const pendiente = getPendiente(empId, row)
                  const estaGuardando = Boolean(guardando[empId])

                  return (
                    <tr
                      key={empId}
                      className={pendiente.habilita ? styles.filaHabilitada : undefined}
                    >
                      <td className="align-middle">{nombreEmpresa(row)}</td>
                      <td className="align-middle">
                        <code>{row.empId}</code>
                      </td>
                      <td className="text-center align-middle">
                        {puedeAdministrar ? (
                          <Toggle
                            toggleOn={pendiente.habilita}
                            onToggleClick={() => handleToggle(empId, row)}
                          />
                        ) : (
                          <i
                            className={
                              pendiente.habilita
                                ? `bi bi-check-lg text-success`
                                : `bi bi-x-lg text-danger`
                            }
                            aria-label={pendiente.habilita ? 'Habilitado' : 'No habilitado'}
                          />
                        )}
                      </td>
                      <td className="align-middle">
                        {puedeAdministrar ? (
                          <Form.Select
                            size="sm"
                            value={pendiente.condicionPago ?? ''}
                            onChange={(e) => handleCondicionPago(empId, row, e.target.value)}
                            aria-label={`Condición de pago para ${nombreEmpresa(row)}`}
                          >
                            <option value="">— Sin condición —</option>
                            {CONDICIONES_PAGO.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </Form.Select>
                        ) : pendiente.condicionPago ? (
                          (CONDICIONES_PAGO.find((c) => c.value === pendiente.condicionPago)
                            ?.label ?? pendiente.condicionPago)
                        ) : (
                          '— Sin condición —'
                        )}
                      </td>
                      {puedeAdministrar && (
                        <td className="text-center align-middle">
                          <Button
                            variant="success"
                            size="sm"
                            className={styles.botonGuardarFila}
                            onClick={() => handleGuardarFila(empId, row)}
                            disabled={estaGuardando || !pendiente.puedeGuardar}
                            aria-label={`Guardar cambios de ${nombreEmpresa(row)}`}
                          >
                            {estaGuardando ? (
                              <Spinner animation="border" size="sm" aria-label="Guardando" />
                            ) : (
                              'Guardar'
                            )}
                          </Button>
                        </td>
                      )}
                    </tr>
                  )
                })
              )}
            </tbody>
          </Table>
        </div>

        <div className={`d-flex justify-content-between align-items-center ${styles.paginacion}`}>
          <span className="text-muted small">
            Página {paginaActual} de {totalPaginas} — {total} empresa{total !== 1 ? 's' : ''}
          </span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              className={styles.botonPaginacion}
              disabled={paginaActual === 1 || cargando}
              onClick={() => setPaginaActual((p) => p - 1)}
            >
              ‹ Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className={styles.botonPaginacion}
              disabled={paginaActual >= totalPaginas || cargando}
              onClick={() => setPaginaActual((p) => p + 1)}
            >
              Siguiente ›
            </Button>
          </div>
        </div>
      </BordePrincipal>
    </section>
  )
}

export default CuentaCorrienteMaestro
